import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { memberArInvoiceHandler } from "../../../../redux/handlers";

import { AllderTable } from "../../../../components/AllderTable";

// ─── Status config ─────────────────────────────────────────────
const STATUS_MAP = {
  DRAFT: { label: "แบบร่าง", cls: "!text-[#949CAC] !bg-[#EDEFF1]" },
  PENDING_APPROVAL: {
    label: "รออนุมัติ",
    cls: "!text-[#FDB428] !bg-[#FFF6E5]",
  },
  AWAITING_PAYMENT: {
    label: "รอรับชำระ",
    cls: "!text-[#8E94FF] !bg-[#ECEDFF]",
  },
  PAID: { label: "ชำระแล้ว", cls: "!text-[#72E128] !bg-[#EEFBE5]" },
  CANCELED: {
    label: "ยกเลิก",
    cls: "!text-[#FF1D1D] !bg-[#FFFFFF] !border !border-[#FF1D1D]",
  },
};

function fmt(value) {
  if (value == null) return "0.00";
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function fmtDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function InvoiceTab() {
  const { businessSlug, customerID } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");

  const debouncedSetFilter = useMemo(
    () => debounce((value) => setFilterText(value), 600),
    [],
  );

  useEffect(() => () => debouncedSetFilter.cancel(), [debouncedSetFilter]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);

  const handleExport = useCallback(() => {
    dispatch(
      memberArInvoiceHandler.getMemberArInvoiceExcel(businessSlug, {
        member_id: customerID,
        search: filterText || undefined,
      }),
    );
  }, [dispatch, businessSlug, customerID, filterText]);

  const fetchInvoices = useCallback(async () => {
    if (!businessSlug || !customerID) return;
    try {
      setLoading(true);
      const res = await dispatch(
        memberArInvoiceHandler.getMemberArInvoicePagination(businessSlug, {
          member_id: customerID,
          page: paginationModel.page + 1,
          per_page: paginationModel.pageSize,
          search: filterText || undefined,
        }),
      );
      if (res) {
        setInvoices(res.data ?? []);
        setRowCount(res.meta?.total ?? res.total ?? 0);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    businessSlug,
    customerID,
    paginationModel.page,
    paginationModel.pageSize,
    filterText,
  ]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const columns = [
    {
      field: "document_no",
      headerName: "เลขที่ใบแจ้งหนี้",
      width: 190,
      sortable: false,
      renderCell: ({ row }) => (
        <Link
          to={`/${businessSlug}/report/debtor/manage/${row.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#565DFF",
            fontWeight: 500,
            textDecoration: "underline",
          }}
        >
          {row.document_no}
        </Link>
      ),
    },
    {
      field: "issue_date",
      headerName: "วันที่ออกเอกสาร",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => fmtDate(row.issue_date),
    },
    {
      field: "due_date",
      headerName: "กำหนดชำระ",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => fmtDate(row.due_date),
    },
    {
      field: "items_count",
      headerName: "จำนวนรายการ",
      width: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => row._count?.items ?? row.items?.length ?? 0,
    },
    {
      field: "pretax_amount",
      headerName: "มูลค่าก่อนภาษี",
      width: 140,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) =>
        fmt(
          (Number(row.taxable_amount_has_vat) || 0) +
            (Number(row.taxable_amount_exempt) || 0) +
            (Number(row.taxable_amount_zero_vat) || 0),
        ),
    },
    {
      field: "vat_amount",
      headerName: "มูลค่าภาษี",
      width: 130,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.vat_amount),
    },
    {
      field: "grand_total",
      headerName: "ยอดสุทธิ",
      width: 130,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.grand_total),
    },
    {
      field: "createdByUserBusinessGroup",
      headerName: "ผู้ออกเอกสาร",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => {
        const ubg = row.createdByUserBusinessGroup;
        if (!ubg) return "-";
        return `${ubg.first_name || ""} ${ubg.last_name || ""}`.trim() || "-";
      },
    },
    {
      field: "status",
      headerName: "สถานะ",
      width: 145,
      sortable: false,
      renderCell: ({ row }) => {
        const s = STATUS_MAP[row.status] ?? { label: row.status, cls: "" };
        return (
          <Chip
            label={s.label}
            variant={row.status === "CANCELED" ? "outlined" : "filled"}
            className={`!w-[120px] ${s.cls}`}
          />
        );
      },
    },
  ];

  return (
    <div className="card card-st-2">
      <div className="card-body px-0 pt-4 pb-0">
        <div className="d-flex justify-content-between align-items-center px-4 mb-3 flex-wrap gap-2">
          <div>
            <button className="btn btn-export-st-1" onClick={handleExport}>
              Export
            </button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="mx-4">
              <input
                type="text"
                className="form-control form-st-4"
                placeholder="ค้นหาเลขที่ใบแจ้งหนี้"
                onChange={(e) => debouncedSetFilter(e.target.value)}
              />
            </div>
            <Button
              variant="contained"
              color="blue3"
              onClick={() =>
                navigate(
                  `/${businessSlug}/report/debtor/manage/new?member_id=${customerID}`,
                )
              }
            >
              สร้างใบแจ้งหนี้
            </Button>
          </div>
        </div>

        <AllderTable
          rows={invoices}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
        />
      </div>
    </div>
  );
}
